import 'leaflet/dist/leaflet.css';

import type { MemberMapArea } from '@growup/accounts-shared';
import L from 'leaflet';
import { useEffect, useRef } from 'react';

export type MapView = 'portugal' | 'acores' | 'madeira' | 'mundo';

export const MAP_VIEWS: Record<MapView, { label: string; center: [number, number]; zoom: number }> =
  {
    portugal: { label: 'Portugal', center: [39.6, -8.0], zoom: 6 },
    acores: { label: 'Açores', center: [38.4, -28.0], zoom: 7 },
    madeira: { label: 'Madeira', center: [32.75, -16.95], zoom: 9 },
    mundo: { label: 'Mundo', center: [30, -10], zoom: 2 },
  };

/** Circle radius in pixels: area grows with the count, so the eye reads it right. */
export function radiusFor(count: number, max: number): number {
  return Math.round(5 + 25 * Math.sqrt(count / Math.max(max, 1)));
}

/**
 * OpenStreetMap with one circle per area. Only counts reach this component;
 * the tiles come from tile.openstreetmap.org and see nothing but map squares.
 */
export function MemberMap({
  areas,
  view,
  selected,
  onSelect,
}: {
  areas: MemberMapArea[];
  view: MapView;
  selected: string | null;
  onSelect: (key: string) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (!box.current) return;
    const m = L.map(box.current, { worldCopyJump: true, scrollWheelZoom: false });
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 13,
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · códigos postais © <a href="https://www.geonames.org/">GeoNames</a> (CC BY 4.0)',
    }).addTo(m);
    map.current = m;
    layer.current = L.layerGroup().addTo(m);
    return () => {
      m.remove();
      map.current = null;
    };
  }, []);

  useEffect(() => {
    const v = MAP_VIEWS[view];
    map.current?.setView(v.center, v.zoom);
  }, [view]);

  useEffect(() => {
    const group = layer.current;
    if (!group) return;
    group.clearLayers();
    const max = Math.max(0, ...areas.map((a) => a.count));
    for (const area of areas) {
      L.circleMarker([area.lat, area.lon], {
        radius: radiusFor(area.count, max),
        className: area.key === selected ? 'gu-map-dot gu-map-dot--on' : 'gu-map-dot',
      })
        .bindTooltip(`${area.label}: ${String(area.count)}`)
        .on('click', () => onSelect(area.key))
        .addTo(group);
    }
  }, [areas, selected, onSelect]);

  useEffect(() => {
    const area = areas.find((a) => a.key === selected);
    if (area && map.current) {
      map.current.setView([area.lat, area.lon], Math.max(map.current.getZoom(), 8));
    }
  }, [areas, selected]);

  return (
    <div
      ref={box}
      className="h-[28rem] w-full rounded border border-gu-line lg:h-[36rem]"
      role="region"
      aria-label="Mapa de membros"
    />
  );
}
